import { CheckCircle, ArrowRight, MapPin, Users, Wine, ShoppingBasket } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ExploreItemWithDistance } from "@/types/explore";

interface ExploreResultCardProps {
  item: ExploreItemWithDistance;
  onClick?: () => void;
  isSelected?: boolean;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Unified result card for both producers and places in Explore.
 * Visually identical - users shouldn't notice the difference.
 */
const ExploreResultCard = ({
  item,
  onClick,
  isSelected,
  isHighlighted,
  onMouseEnter,
  onMouseLeave,
}: ExploreResultCardProps) => {
  const distanceDisplay = item._distanceKm
    ? item._distanceKm < 1
      ? `${Math.round(item._distanceKm * 1000)} m`
      : `${item._distanceKm.toFixed(1)} km`
    : null;

  // Producer count from the places_explore view
  const producerCount = item.producerCount || 0;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const cardClasses = cn(
    "group relative bg-card rounded-xl overflow-hidden transition-all duration-200 cursor-pointer",
    "border",
    isSelected
      ? "border-primary/50 bg-primary/5 shadow-md ring-1 ring-primary/20"
      : isHighlighted
        ? "border-primary/30 bg-primary/5 shadow-sm"
        : "border-transparent hover:border-border/50 hover:shadow-sm"
  );

  return (
    <article
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cardClasses}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      data-item-id={item.id}
    >
      {/* Cover image */}
      {item.coverImageUrl ? (
        <div className="aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={item.coverImageUrl}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
          <span className="text-4xl text-muted-foreground/30">
            {item.name.charAt(0)}
          </span>
        </div>
      )}

      {/* Distance badge */}
      {distanceDisplay && (
        <div className="absolute top-3 right-3">
          <span className="text-xs font-medium text-primary bg-background/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {distanceDisplay}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-2">
        <div>
          <h3 className="font-display text-lg font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {item.name}
          </h3>
          {item.subtitle && (
            <p className="text-sm text-muted-foreground">{item.subtitle}</p>
          )}
        </div>

        {item.shortDescription && (
          <p className="text-sm text-foreground/75 line-clamp-2">
            {item.shortDescription}
          </p>
        )}

        {/* Signal chip (max 1, prioritized) */}
        {(item.hasFarmSaleAlcohol || item.hasFarmShop) && (
          <div className="flex items-center gap-1.5">
            {item.hasFarmSaleAlcohol ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Dryck
              </span>
            ) : item.hasFarmShop ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Handla från gården
              </span>
            ) : null}
          </div>
        )}

        {/* Producer badge */}
        {producerCount > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-primary">
            <Users className="h-3.5 w-3.5" />
            <span>
              {producerCount === 1 ? "1 värd" : `${producerCount} värdar`}
            </span>
          </div>
        )}

        {/* Tags/Categories */}
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs bg-muted/50 text-muted-foreground px-2 py-0.5 rounded-full"
              >
                {tag}
              </span>
            ))}
            {item.tags.length > 3 && (
              <span className="text-xs text-muted-foreground">
                +{item.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="pt-2 flex items-center justify-between border-t border-border/20">
          <span className="text-xs text-muted-foreground/60 inline-flex items-center gap-1">
            {item.verified && (
              <>
                <CheckCircle className="h-3 w-3 text-primary/60" />
                Verifierad
              </>
            )}
          </span>
          <span className="inline-flex items-center gap-1 text-sm text-primary group-hover:gap-2 transition-all">
            Visa
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </article>
  );
};

export default ExploreResultCard;
